import { DoNotCare } from "../../types";
import { objectLookup } from "../../utils";

/**
 * Checks if the given value contains the search term (case-insensitive).
 * @param value The string or number to check
 * @param searchTerm The term to search for
 * @returns The original value as a string if it contains the search term, otherwise undefined
 */
const stringMatch = (
  value: string | number,
  searchTerm: string
): string | undefined => {
  const strVal = String(value).toLowerCase();
  if (strVal.includes(searchTerm.toLowerCase())) {
    return strVal;
  }
  return undefined;
};

/**
 * Finds the best matching string value from the data based on the search term and optional paths.
 * @param data The data to search within
 * @param searchTerm The term to search for
 * @param paths Optional paths to look into the data
 * @returns The best matching string value or undefined if no match is found
 */
const getTopMatchStringValue = (
  data: DoNotCare,
  searchTerm: string,
  paths?: string[]
): string | undefined => {
  const matches = (
    paths
      ? paths.map(path => stringMatch(objectLookup(data, path), searchTerm))
      : [stringMatch(data, searchTerm)]
  )
    .filter(v => v !== undefined)
    .sort((a, b) => a.length - b.length);

  return matches[0];
};

/**
 * Performs an exact match search on the given data and keys and search term
 * Results are sorted to prioritize exact matches first, then partial matches
 * @param data Data to search
 * @param keys Fields to include in the search. Can be a path, e.g., "myArray.label"
 * @param searchTerm The term to search for
 * @returns An array of the data that matches the search term, sorted with exact matches first
 */
export const includesSearch = <T extends Record<string, DoNotCare> | string>(
  data: T[] = [],
  keys: string[] | string,
  searchTerm: string | undefined | null
): T[] => {
  if (searchTerm && data.length) {
    let normalizedKeys = Array.isArray(keys) ? keys : [keys];

    /**
     * If no keys are passed in, and first item is typeof string,
     * we normalize the keys to be an array of 1 empty string.
     * The empty string will evaluate to false,
     * and recursiveExactMatchSearch will search on the string passed in.
     */
    if (normalizedKeys.filter(k => !!k).length === 0) {
      if (typeof data[0] !== "string") {
        return data;
      }
      normalizedKeys = [""];
    }

    return data
      .filter(
        item => !!getTopMatchStringValue(item, searchTerm, normalizedKeys)
      )
      .sort((a, b) => {
        const aVal = getTopMatchStringValue(a, searchTerm, normalizedKeys);
        const bVal = getTopMatchStringValue(b, searchTerm, normalizedKeys);
        const aValStartsWithTerm = aVal?.startsWith(searchTerm.toLowerCase());
        const bValStartsWithTerm = bVal?.startsWith(searchTerm.toLowerCase());
        if (aValStartsWithTerm && !bValStartsWithTerm) {
          return -1;
        }
        if (!aValStartsWithTerm && bValStartsWithTerm) {
          return 1;
        }
        if (aVal && bVal) {
          return aVal.length - bVal.length;
        }
        if (aVal) {
          return -1;
        }
        if (bVal) {
          return 1;
        }
        return 0;
      });
  }
  return data;
};
